import type { Distance, SpatialRectangle } from "@pdfking/math"
import { Floor, Min, Scale, Subtract, Total } from "@pdfking/math"
import { ZERO_DISTANCE } from "@pdfking/math/constants"
import type { ColorID, PageShape } from "@pdfking/renderer-api"
import { HorizontalAlign, PlotFunction } from "@pdfking/renderer-api"
import { NullQ } from "@pdfking/structs"

import { PALETTE } from "#constants"

import { PlotLine } from "../layout/PlotLine"
import { PlotNotebookBackground } from "../layout/PlotNotebookBackground"
import { PlainText } from "../text/PlainText"
import { NotebookBackground } from "../types/NotebookBackground"

// The cell for its name, on the box's width, and its lines
const CELL = 0.15
const CELL_LINES = 3

/** A block of an agenda's page filling `box`, `pitch` its lines apart, as the
 * shop's perpetual agenda has it: `label` in `color` at the foot of a cell
 * CELL of the box wide and CELL_LINES lines tall, or as many as it has, over it a day's `number`,
 * large, on a square of the primary when `badge`; lines ruled beside the
 * cell from the box's top and across the box from its foot down to the
 * box's, on one ruling when the box is whole lines tall. */
export const DrawAgendaBlock = ({
  badge,
  box,
  color,
  label,
  number,
  pitch,
}: {
  badge: boolean
  box: SpatialRectangle<Distance>
  color: ColorID
  label: string
  number: string | undefined
  pitch: Distance
}): PageShape[] => {
  const top = Total(box.y, box.height)
  const cell = Scale(box.width, CELL)
  // a short block's as many as it has
  const lines = Min(CELL_LINES, Floor(box.height / pitch))
  const foot = Subtract(top, Scale(pitch, lines))
  const font = Scale(pitch, 0.4)
  // The number's square, on the cell's middle over the name's line
  const side = Scale(pitch, 1.5)
  const square = {
    height: side,
    width: side,
    x: Total(box.x, Scale(Subtract(cell, side), 0.5)),
    y: Subtract(top, Scale(pitch, 1.75)),
  }
  const number_font = Scale(side, 0.7)
  return [
    ...PlotNotebookBackground({
      background: NotebookBackground.LINES,
      // from the block's top, a pitch over it its first, down to the line
      // over the foot
      box: {
        height: Scale(pitch, lines + 0.5),
        width: Subtract(box.width, cell),
        x: Total(box.x, cell),
        y: Total(foot, Scale(pitch, 0.5)),
      },
      pitch,
    }),
    PlotLine(Total(box.x, cell), foot, ZERO_DISTANCE, Scale(pitch, lines)),
    ...(!NullQ(number) && badge
      ? [
          {
            enum: PlotFunction.PLOT_RECT,
            fill_color: PALETTE.primary,
            position: square,
          } as PageShape,
        ]
      : []),
    ...(NullQ(number)
      ? []
      : [
          PlainText(
            number,
            square.x,
            square.width,
            Total(square.y, Scale(Subtract(side, number_font), 0.5)),
            number_font,
            badge ? PALETTE.paper : color,
            HorizontalAlign.CENTER
          ),
        ]),
    PlainText(
      label,
      box.x,
      cell,
      Total(foot, Scale(font, 0.6)),
      font,
      color,
      HorizontalAlign.CENTER
    ),
    ...PlotNotebookBackground({
      background: NotebookBackground.LINES,
      // from the cell's foot down to the block's, the next block's top
      box: {
        ...box,
        height: Total(Subtract(foot, box.y), Scale(pitch, 1.5)),
        y: Subtract(box.y, Scale(pitch, 0.5)),
      },
      pitch,
    }),
  ]
}
